import {Auth, Hub} from 'aws-amplify';
import * as Sentry from '@sentry/react';
import {SETTINGS} from '../settings';
import CacheData from './cacheData';
import {BllinkLogger} from './bllink_loggers';
import {isMobileView, MOBILE_MAX_WIDTH} from './isMobileView';
import {isMobile as isMobileDevice} from 'react-device-detect';

/**
 * where a manager lands right after logging in.
 *
 * the decision is made at the login itself and nowhere else, so no page has to undo a landing it
 * has already started rendering.
 * */

/**
 * the mobile manager app is served from /m on the same origin, so the cognito session carries over
 * and no second login is needed
 * */
const MOBILE_APP_URL = '/m/';

const SOCIAL_LOGIN_PENDING_KEY = 'socialLoginPending';

/**
 * TEMPORARY - the mobile app is opening up partner by partner and will end up open to every manager.
 * when it does, drop both lists together with the two functions under them: the whole decision
 * collapses into `isMobileView()`, and nothing else here has to change.
 *
 * two lists, because which one a partner belongs in is not a style choice - it is where that
 * partner can be read from at all.
 * */

/**
 * real management companies, spelled the way the profile reports them: the partner's externalID.
 * case does not matter, paste them however the admin screens show them.
 * */
const MOBILE_APP_PARTNER_EXTERNAL_IDS = [];

/**
 * partners matched on the building instead, by internal numeric id.
 *
 * partner 47 can only be matched this way. a user record is never allowed to point at it - Users
 * has a beforeCreate/beforeUpdate hook that throws on `partnerID === 47` - so it cannot appear in
 * a profile at all, under any spelling. it sits on the building, through PartnerAccounts, and that
 * is the only place it can be read from.
 * */
const MOBILE_APP_PARTNER_INTERNAL_IDS = [47];

/**
 * why the handoff did not happen. the last two mean we could not tell whether this manager
 * qualifies, as opposed to knowing that they do not - those are the ones worth reporting, because
 * from the landing alone they are indistinguishable from working as intended.
 * */
const SKIPPED = {
    notMobileView: 'not a mobile view',
    partnerNotListed: 'partner is not on the mobile app list',
    profileNotReady: 'profile came back with no userID',
    noBuildingPartner: 'no partner could be read off a building',
};

/** the two above that mean "could not tell", as opposed to "told, and the answer is no" */
const UNRESOLVED = [SKIPPED.profileNotReady, SKIPPED.noBuildingPartner];

/**
 * a read that cannot change anything.
 *
 * deliberately not ServerData: its shared error handler reacts to a failed call on its own - a 302
 * signs the manager out and deletes their cognito user, a 403 redirects, some ClientErrors alert in
 * hebrew and navigate to all-buildings. those belong to a page acting on the manager's behalf, not
 * to a probe that is only deciding where to send them. so this asks with plain fetch and returns
 * what came back, including the failure, for the caller to report.
 *
 * @return {Promise<{status: number|null, body: any, error?: string}>}
 * */
async function probe(path, accessToken) {
    try {
        const response = await fetch(`${SETTINGS.server.baseUrl}${path}`, {
            headers: {Authorization: `Bearer ${accessToken}`},
        });
        let body = null;
        try {
            body = await response.json();
        } catch (e) {
            // a non json body is itself worth seeing in the report
        }
        return {status: response.status, body};
    } catch (e) {
        return {status: null, body: null, error: e && e.message};
    }
}

/**
 * the partner of the building this manager manages.
 *
 * only asked when the profile names no partner, which is what an individual manager looks like:
 * their partner is on the building, not on their user record. one building belongs to exactly one
 * partner - PartnerAccounts is unique on ptopID - so this is a lookup, not a guess.
 *
 * asks for the building without its stats and its tenants. those are the expensive half of that
 * endpoint, and the partner is resolved separately from them server side, so dropping them costs
 * us nothing here.
 *
 * the manager's first building stands for the rest. an individual manager has one, which is the
 * case this exists for.
 *
 * @return {Promise<number|null>} internal partner id
 * */
async function getBuildingPartnerID(accessToken, trail) {
    const list = await probe('/managers/buildings/', accessToken);
    trail.buildingsStatus = list.status;
    trail.buildingsError = list.error ?? null;
    trail.buildingsBodyKeys = Object.keys(list.body || {});
    trail.buildingsCount = Array.isArray(list.body?.buildings) ? list.body.buildings.length : null;
    trail.buildingsPartnerFromList = list.body?.partner?.partnerID ?? null;

    const buildingID = list.body?.buildings?.[0]?.buildingID ?? null;
    trail.firstBuildingID = buildingID;
    if (!buildingID) {
        return null;
    }

    const one = await probe(
        `/managers/buildings/${buildingID}?skipStats=true&skipTenantDetails=true&skipDisabledCheck=true`,
        accessToken,
    );
    trail.buildingStatus = one.status;
    trail.buildingError = one.error ?? null;
    trail.buildingBodyKeys = Object.keys(one.body?.building || {});
    trail.buildingPartnerName = one.body?.building?.partnerName ?? null;
    return one.body?.building?.partnerID ?? null;
}

/**
 * @param {string} accessToken
 * @param {{userID?: string, partnerID?: string}} profile
 * @return {Promise<{useMobileApp: boolean, skipped?: string}>}
 * */
async function shouldUseMobileApp(accessToken, profile, trail) {
    if (!isMobileView()) {
        return {useMobileApp: false, skipped: SKIPPED.notMobileView};
    }
    // no userID means the server does not recognise this manager yet
    if (!profile?.userID) {
        return {useMobileApp: false, skipped: SKIPPED.profileNotReady};
    }
    // a real management company is named in the profile already, and costs nothing more to check
    if (profile.partnerID) {
        const partnerID = profile.partnerID.trim().toLowerCase();
        const listed = MOBILE_APP_PARTNER_EXTERNAL_IDS.some(id => id.trim().toLowerCase() === partnerID);
        return listed ? {useMobileApp: true} : {useMobileApp: false, skipped: SKIPPED.partnerNotListed};
    }
    // no partner on the user record. that is either an individual manager, whose partner is on the
    // building, or someone holding a building permission without belonging to a company. only the
    // building tells those two apart - the profile cannot
    if (MOBILE_APP_PARTNER_INTERNAL_IDS.length === 0) {
        return {useMobileApp: false, skipped: SKIPPED.partnerNotListed};
    }
    const buildingPartnerID = await getBuildingPartnerID(accessToken, trail);
    trail.buildingPartnerID = buildingPartnerID;
    if (buildingPartnerID == null) {
        return {useMobileApp: false, skipped: SKIPPED.noBuildingPartner};
    }
    return MOBILE_APP_PARTNER_INTERNAL_IDS.includes(buildingPartnerID)
        ? {useMobileApp: true}
        : {useMobileApp: false, skipped: SKIPPED.partnerNotListed};
}

/**
 * BllinkLogger is off in production (settings.logger.logLevel), so anything reported through it
 * here is lost exactly when we need it. these go to Sentry instead, carrying enough of the trail to
 * name the step that failed without another reproduction.
 *
 * no email and no name: what tells us what happened is the shape of each answer, and Sentry already
 * carries whatever user context the app set.
 * */
function report(skipped, trail) {
    Sentry.captureMessage(`post login landing: ${skipped}`, {
        level: 'warning',
        tags: {feature: 'mobileAppHandoff', skipped, source: trail.source},
        extra: {...trail, skipped},
    });
}

/**
 * asked here, with the token the login just issued, instead of waiting for the manager page to ask
 * for it later.
 *
 * on a good answer it goes into the same cache key getUserProfile reads (5 hours), so that later
 * fetch is served from cache and never reaches the server. only a 200 is cached - caching anything
 * else would hand the manager page a broken profile it would otherwise have refetched.
 * */
async function getManagerProfile(accessToken, trail) {
    const result = await probe('/managers/profile', accessToken);
    trail.profileStatus = result.status;
    trail.profileError = result.error ?? null;
    trail.profileKeys = Object.keys(result.body || {});
    trail.profileHasUserID = !!result.body?.userID;
    trail.profileHasPartnerID = !!result.body?.partnerID;
    trail.profileRedirect = result.body?.redirect ?? null;

    if (result.status === 200 && result.body) {
        CacheData.cache('profile', result.body);
    }
    return result.body;
}

/**
 * where a manager goes now that they are logged in, and the redirect itself.
 *
 * @param {string} accessToken token issued by the login that just succeeded
 * @param {string|null} defaultUrl where this login would have gone on its own. null means the
 *        manager is already on that page (social login comes back to it), so only a handoff to the
 *        mobile app is worth a navigation.
 * @param {string} source which of the three logins this is, so a report names the path
 * @return {Promise<boolean>} true when the manager was sent to the mobile app
 * */
export async function goToPostLoginPage(accessToken, defaultUrl, source = 'unknown') {
    let url = defaultUrl;
    let usedMobileApp = false;
    const startedAt = Date.now();
    // the two halves of isMobileView are kept apart here: which one carried the decision is the
    // difference between a phone and a narrow desktop window, and they are not interchangeable
    const trail = {
        source,
        isMobileDevice,
        viewportWidth: window.innerWidth,
        mobileMaxWidth: MOBILE_MAX_WIDTH,
        defaultUrl,
    };

    try {
        const profile = await getManagerProfile(accessToken, trail);
        const decision = await shouldUseMobileApp(accessToken, profile, trail);
        trail.elapsedMs = Date.now() - startedAt;
        if (decision.useMobileApp) {
            url = MOBILE_APP_URL;
            usedMobileApp = true;
        } else if (UNRESOLVED.includes(decision.skipped)) {
            report(decision.skipped, trail);
        }
    } catch (e) {
        // the landing page fetches the profile itself, so failing here costs the mobile app handoff
        // and nothing more - the manager still lands where this login was going anyway
        BllinkLogger.error('could not decide the post login landing page', e);
        trail.elapsedMs = Date.now() - startedAt;
        Sentry.captureException(e, {
            tags: {feature: 'mobileAppHandoff', skipped: 'threw', source},
            extra: trail,
        });
    }

    if (url) {
        window.location.href = url;
    }
    return usedMobileApp;
}

/**
 * social login hands the browser to cognito, so there is no moment to decide anything before
 * leaving. this flag is what lets us recognise the way back.
 * */
export function markSocialLoginPending() {
    window.localStorage.setItem(SOCIAL_LOGIN_PENDING_KEY, 'true');
}

async function handleSocialLoginReturn() {
    if (window.localStorage.getItem(SOCIAL_LOGIN_PENDING_KEY) !== 'true') {
        return;
    }
    // taken before the await so the boot call and the amplify event below cannot both act on it
    window.localStorage.removeItem(SOCIAL_LOGIN_PENDING_KEY);

    let accessToken;
    try {
        const user = await Auth.currentAuthenticatedUser();
        accessToken = user.signInUserSession.accessToken.jwtToken;
    } catch (e) {
        // amplify has not finished with the cognito redirect yet, or the manager walked away from
        // the cognito page and came back on their own. put the flag back: if it was the first, the
        // amplify event below runs this again, and if it was the second nothing else will.
        markSocialLoginPending();
        return;
    }

    // cognito sends the manager to the all buildings page, which is where they already are, so
    // there is no default url to navigate to here
    await goToPostLoginPage(accessToken, null, 'social');
}

/**
 * call once when the app boots. does nothing unless the manager is coming back from a social login.
 * @return {function} unsubscribe
 * */
export function watchForSocialLoginReturn() {
    // registered first so it catches a login that completes while the call below is still waiting
    const stopListening = Hub.listen('auth', ({payload}) => {
        if (payload?.event === 'signIn' || payload?.event === 'cognitoHostedUI') {
            handleSocialLoginReturn();
        }
    });
    handleSocialLoginReturn();
    return stopListening;
}
